import { cameraAnalysesQuery, siteAnalysesQuery } from '@/api/queries'
import type { AnalysisEntry } from '@/api/schemas'
import { AnalysesList } from '@/components/site/AnalysesList'
import { ErrorCard, LoadingCard } from '@/components/ui/StateCards'
import type { SalaIntent } from '@/lib/viewIntent'
import { useQuery } from '@tanstack/react-query'
import { t } from '@/lib/i18n'

type AnalysesIntent = Extract<SalaIntent, { kind: 'analyses' }>

export function AnalysesBody({ intent }: { intent: AnalysesIntent }) {
  // V1 uses a single date. Backend doesn't support a true range on
  // /analyses yet; when the assistant emits a range we pick the end-date
  // (most recent) so the user still sees something useful. date_from is a
  // second fallback for the same reason.
  const date = intent.date ?? intent.date_to ?? intent.date_from ?? null
  if (intent.camera_id) {
    return (
      <CameraAnalyses
        siteId={intent.site_id}
        cameraId={intent.camera_id}
        date={date}
      />
    )
  }
  return <SiteAnalyses siteId={intent.site_id} date={date} />
}

function SiteAnalyses({ siteId, date }: { siteId: string; date: string | null }) {
  const q = useQuery(siteAnalysesQuery(siteId, date))
  if (q.isPending) return <LoadingCard label={t('Loading analyses…')} />
  if (q.isError || !q.data) return <ErrorCard message={t('Could not load analyses.')} />
  return <AnalysesList siteId={siteId} entries={q.data.entries} selectedDate={date} />
}

function CameraAnalyses({
  siteId,
  cameraId,
  date,
}: {
  siteId: string
  cameraId: string
  date: string | null
}) {
  const q = useQuery(cameraAnalysesQuery(siteId, cameraId, date))
  if (q.isPending) return <LoadingCard label={t('Loading analyses…')} />
  if (q.isError || !q.data) return <ErrorCard message={t('Could not load analyses.')} />
  const entries = q.data.entries as AnalysisEntry[]
  return <AnalysesList siteId={siteId} entries={entries} selectedDate={date} />
}
